import { Controller } from "@hotwired/stimulus";

/**
 * Weight Chart Controller
 *
 * Manages the weight progress chart for exercise templates
 */
export default class extends Controller {
  static targets = ["canvas"];

  connect() {
    this.createChart();

    // Watch for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "data-bs-theme") {
          this.updateChartColors();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
  }

  createChart() {
    if (!this.hasCanvasTarget) return;

    // Check if data is available
    const dataElement = this.element.querySelector("[data-chart-data]");
    if (!dataElement) return;

    const chartData = JSON.parse(dataElement.dataset.chartData);
    if (!chartData || !chartData.labels || !chartData.data) return;

    // Define chart colors based on the current theme
    const isDarkMode =
      document.documentElement.getAttribute("data-bs-theme") === "dark";
    const textColor = isDarkMode ? "#f8f9fa" : "#212529";
    const gridColor = isDarkMode
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.1)";

    // Create the chart
    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels: chartData.labels,
        datasets: [
          {
            label: "Weight Progression",
            data: chartData.data,
            borderColor: "#0d6efd",
            backgroundColor: "rgba(13, 110, 253, 0.1)",
            borderWidth: 2,
            pointBackgroundColor: "#0d6efd",
            pointBorderColor: isDarkMode ? "#212529" : "#fff",
            pointRadius: 4,
            tension: 0.2,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: {
              color: gridColor,
            },
            ticks: {
              color: textColor,
            },
          },
          y: {
            beginAtZero: false,
            grid: {
              color: gridColor,
            },
            ticks: {
              color: textColor,
            },
          },
        },
        plugins: {
          legend: {
            labels: {
              color: textColor,
            },
          },
          tooltip: {
            backgroundColor: isDarkMode ? "#343a40" : "#fff",
            titleColor: isDarkMode ? "#f8f9fa" : "#212529",
            bodyColor: isDarkMode ? "#f8f9fa" : "#212529",
            borderColor: isDarkMode ? "#495057" : "#dee2e6",
            borderWidth: 1,
          },
        },
      },
    });
  }

  updateChartColors() {
    if (!this.chart) return;

    const isDarkMode =
      document.documentElement.getAttribute("data-bs-theme") === "dark";
    const textColor = isDarkMode ? "#f8f9fa" : "#212529";
    const gridColor = isDarkMode
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.1)";

    // Update dataset point colors
    this.chart.data.datasets.forEach((dataset) => {
      dataset.pointBorderColor = isDarkMode ? "#212529" : "#fff";
    });

    // Update scales
    this.chart.options.scales.x.grid.color = gridColor;
    this.chart.options.scales.x.ticks.color = textColor;
    this.chart.options.scales.y.grid.color = gridColor;
    this.chart.options.scales.y.ticks.color = textColor;

    // Update plugins
    this.chart.options.plugins.legend.labels.color = textColor;
    this.chart.options.plugins.tooltip.backgroundColor = isDarkMode
      ? "#343a40"
      : "#fff";
    this.chart.options.plugins.tooltip.titleColor = isDarkMode
      ? "#f8f9fa"
      : "#212529";
    this.chart.options.plugins.tooltip.bodyColor = isDarkMode
      ? "#f8f9fa"
      : "#212529";
    this.chart.options.plugins.tooltip.borderColor = isDarkMode
      ? "#495057"
      : "#dee2e6";

    // Update the chart
    this.chart.update();
  }
}
